/**
 * Área CLIENTES: a lista de clientes do admin, a escolha do atendimento e a exclusão.
 *
 * Cobre as chamadas do legado `portal/admin.html` que a aba Clientes faz:
 *   · a lista, em três leituras (`3542`, `3556` e `3574`), que a tela junta com a mesma regra de
 *     `carregarMapaClientesLead` (`3541-3614`);
 *   · os leads da cliente clicada, para decidir se abre direto ou pergunta "Qual atendimento?"
 *     (`5876`);
 *   · a exclusão pela Edge Function `excluir-cliente` (`4804`).
 *
 * A JUNÇÃO DAS TRÊS LEITURAS NÃO MORA AQUI, e sim em `apps/admin/src/telas/clientes/regras.ts`:
 * regra de negócio não mora no módulo que abre conexão, e a junção é regra pura, testável sem
 * banco. Este módulo só traduz: pede ao Supabase o que o legado pedia, e devolve como veio.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  executar,
  invocarFuncao,
  type FonteDeDados,
  type LinhaLeadsFunil,
  type LinhaPerfisCliente,
  type LinhaQuestionarioGeral,
  type Resultado,
} from '../nucleo.js';

/** O perfil que a lista lê: as três colunas do `select` de `admin.html:3544`. */
export type PerfilDaLista = Pick<LinhaPerfisCliente, 'user_id' | 'email' | 'criado_em'>;

/** O que a lista lê do questionário geral: as quatro colunas de `admin.html:3558`. */
export type QuestionarioDaLista = Pick<
  LinhaQuestionarioGeral,
  'user_id' | 'nome_completo' | 'telefone' | 'origem_cadastro'
>;

/** O que a lista lê de cada lead vinculado: as seis colunas de `admin.html:3576`. */
export type LeadDaLista = Pick<
  LinhaLeadsFunil,
  'user_id_vinculado' | 'produtos' | 'status' | 'email' | 'nome' | 'contato_whatsapp'
>;

/** O lead oferecido na escolha "Qual atendimento?": as quatro colunas de `admin.html:5878`. */
export type LeadParaEscolha = Pick<LinhaLeadsFunil, 'id' | 'produtos' | 'status' | 'data_criacao'>;

/**
 * Todo cadastro do portal, o mais recente primeiro.
 * Legado: `admin.html:3542`, `sb.from('perfis_cliente').select('user_id, email, criado_em')
 * .order('criado_em', { ascending: false })`.
 */
export function lerPerfis(fonte: FonteDeDados): Promise<Resultado<PerfilDaLista[]>> {
  // A ordem decrescente por data de cadastro é a ordem em que a lista aparece na tela.
  return executar<PerfilDaLista[]>(
    clienteDe(fonte).from('perfis_cliente').select('user_id, email, criado_em').order('criado_em', { ascending: false }),
  );
}

/**
 * O perfil de UMA cliente, para a ficha aberta por endereço.
 *
 * O legado não tem esta leitura: ele abria a ficha com o objeto da lista já em memória
 * (`abrirClienteComEscolhaDeLead(cliente)`, `admin.html:4772`). Aqui a ficha tem endereço próprio
 * (`/clientes/<id>`) e pode ser aberta sem a lista carregada, então relê as MESMAS colunas de
 * `admin.html:3544`, filtradas pela cliente. `maybeSingle` porque `user_id` é chave primária e a
 * linha pode não existir (cliente que nunca entrou no portal).
 */
export function lerPerfilDaCliente(fonte: FonteDeDados, userId: string): Promise<Resultado<PerfilDaLista | null>> {
  // Mesmas colunas da lista, e a chave primária no filtro.
  return executar<PerfilDaLista | null>(
    clienteDe(fonte).from('perfis_cliente').select('user_id, email, criado_em').eq('user_id', userId).maybeSingle(),
  );
}

/**
 * Nome, telefone e origem de cadastro das clientes da lista.
 * Legado: `admin.html:3556`, `sb.from('questionario_geral').select('user_id, nome_completo,
 * telefone, origem_cadastro').in('user_id', userIds)`.
 *
 * Lista, e não linha única: a mesma cliente pode ter um questionário por lead, e a junção do
 * legado fica com o ÚLTIMO que o banco devolveu (`forEach` que sobrescreve, `admin.html:3561`).
 */
export function lerQuestionariosDaLista(
  fonte: FonteDeDados,
  userIds: readonly string[],
): Promise<Resultado<QuestionarioDaLista[]>> {
  // O legado só faz esta leitura quando há ao menos uma cliente (`if (userIds.length > 0)`); com
  // a lista vazia o resultado é vazio, sem ir à rede, e a junção não muda.
  if (userIds.length === 0) return Promise.resolve({ ok: true, dados: [] });
  return executar<QuestionarioDaLista[]>(
    clienteDe(fonte)
      .from('questionario_geral')
      .select('user_id, nome_completo, telefone, origem_cadastro')
      // `in` recebe uma cópia mutável, porque o tipo do supabase-js não aceita lista `readonly`.
      .in('user_id', [...userIds]),
  );
}

/**
 * Os leads vinculados às clientes da lista: produtos, status e o contato que vem do Funil.
 * Legado: `admin.html:3574`, `sb.from('leads_funil').select('user_id_vinculado, produtos, status,
 * email, nome, contato_whatsapp').in('user_id_vinculado', userIds)`.
 */
export function lerLeadsDaLista(fonte: FonteDeDados, userIds: readonly string[]): Promise<Resultado<LeadDaLista[]>> {
  // Mesmo atalho do legado: sem cliente, não há lead a procurar.
  if (userIds.length === 0) return Promise.resolve({ ok: true, dados: [] });
  return executar<LeadDaLista[]>(
    clienteDe(fonte)
      .from('leads_funil')
      .select('user_id_vinculado, produtos, status, email, nome, contato_whatsapp')
      // Cópia mutável, pelo mesmo motivo da leitura anterior.
      .in('user_id_vinculado', [...userIds]),
  );
}

/**
 * Os atendimentos (leads) de uma cliente, sem os perdidos, o mais novo primeiro.
 * Legado: `admin.html:5876`, `sb.from('leads_funil').select('id, produtos, status, data_criacao')
 * .eq('user_id_vinculado', cliente.user_id).neq('status', 'perdido')
 * .order('data_criacao', { ascending: false })`.
 *
 * Zero leads abre a ficha sem lead, um abre direto, e dois ou mais perguntam qual: a decisão é
 * da tela, com o número de linhas que volta daqui.
 */
export function lerLeadsParaEscolha(fonte: FonteDeDados, userId: string): Promise<Resultado<LeadParaEscolha[]>> {
  return executar<LeadParaEscolha[]>(
    clienteDe(fonte)
      .from('leads_funil')
      .select('id, produtos, status, data_criacao')
      .eq('user_id_vinculado', userId)
      // Lead perdido não é atendimento: não aparece na escolha nem abre ficha.
      .neq('status', 'perdido')
      // O mais novo primeiro, que é a ordem de chegada antes de a tela subir os em andamento.
      .order('data_criacao', { ascending: false }),
  );
}

/**
 * As clientes que têm ao menos um atendimento numa etapa da jornada.
 *
 * NÃO EXISTE NO LEGADO: é o recorte que o Painel novo manda para a lista (`/clientes?etapa=<etapa>`,
 * regra de gráfico interativo). O Painel conta as linhas de `jornada_cliente` por etapa
 * (`apps/admin/src/telas/painel/fonte.ts:381-386`), então o recorte lê a MESMA tabela com o MESMO
 * filtro, e a lista mostra cada cliente dessas linhas uma vez.
 */
export function lerClientesNaEtapa(fonte: FonteDeDados, etapa: string): Promise<Resultado<{ user_id: string }[]>> {
  return executar<{ user_id: string }[]>(
    // Só a cliente de cada linha: a etapa já está no filtro, e o resto da linha não é usado.
    clienteDe(fonte).from('jornada_cliente').select('user_id').eq('etapa', etapa),
  );
}

/**
 * Exclui a cliente inteira: questionário, arquivos e login.
 * Legado: `admin.html:4804`, `sb.functions.invoke('excluir-cliente', { body: { user_id } })`.
 *
 * A exclusão roda na Edge Function porque apagar o login exige a chave de serviço, que nunca
 * mora no navegador. O erro volta com a mensagem original, que a tela traduz com `traduzirErro`
 * como o legado (`erro.frase`).
 */
export function excluirCliente(fonte: FonteDeDados, userId: string): Promise<Resultado<unknown>> {
  // O corpo é o do legado, só com o `user_id` da cliente.
  return invocarFuncao<unknown>(fonte, 'excluir-cliente', { body: { user_id: userId } });
}
